import { useCallback, useRef } from 'react';

import {
  isChannelSettingsLoadFailed,
  type ChannelSettingsLoad,
  type ChannelSettingsLoadFailureCounts,
} from '../xstate/channel-settings-load-status';

export interface ChannelSettingsLoadState {
  ruleGroupsLoaded: boolean;
  apiCredentialsLoaded: boolean;
  liveAgentLoaded: boolean;
  ruleGroupsFailureCount: number | undefined;
  apiCredentialsFailureCount: number | undefined;
  liveAgentFailureCount: number | undefined;
}

export interface ChannelSettingsLoadFailures {
  ruleGroupsFailed: boolean;
  apiCredentialsFailed: boolean;
  liveAgentFailed: boolean;
  /** Call immediately BEFORE dispatching a load, with the count as it stands. */
  recordDispatch: (
    load: ChannelSettingsLoad,
    failureCount: number | undefined
  ) => void;
}

/**
 * Turns the PROJECT-scoped failure counts into "did MY dispatch of this step
 * fail?" for one mounted reader.
 *
 * The three loads are dispatched from four places — this page and the Data
 * Privacy / Reporting API Credentials / Live Agent Platforms screens — and only
 * this page reads the record. A failure raised on one of the others, or still
 * in flight when this page opened, therefore outlives the page that saw it; read
 * as a bare flag it made this page treat the step as already-settled-and-failed
 * and render "Couldn't load … Reload the page" without having tried once.
 *
 * The remembered count is snapshotted at the DISPATCH, not at mount: while
 * another screen's flow holds the machine this page is not idle and cannot
 * dispatch, so anything recorded before the snapshot is by construction somebody
 * else's, and everything after it is this page's own — the loads are serialized
 * through `idle`.
 *
 * Extracted from the form so it can be unit-tested: the form itself pulls in
 * design-system Lit components and cannot be rendered in this package's test
 * environment, while this hook is React-only.
 */
export const useChannelSettingsLoadFailures = (
  state: ChannelSettingsLoadState
): ChannelSettingsLoadFailures => {
  // A ref, not state: recording a dispatch must not re-render, and must not
  // re-run the effect that does the dispatching. Reading it during render is
  // safe because it only moves when a load is dispatched, and the counts it is
  // compared against arrive through the xstate selector, which re-renders on
  // its own whenever one of them changes.
  const failureCountAtDispatchRef = useRef<ChannelSettingsLoadFailureCounts>(
    {}
  );

  const recordDispatch = useCallback(
    (load: ChannelSettingsLoad, failureCount: number | undefined): void => {
      failureCountAtDispatchRef.current[load] = failureCount ?? 0;
    },
    []
  );

  return {
    ruleGroupsFailed: isChannelSettingsLoadFailed({
      loaded: state.ruleGroupsLoaded,
      failureCount: state.ruleGroupsFailureCount,
      failureCountAtDispatch: failureCountAtDispatchRef.current.ruleGroups,
    }),
    apiCredentialsFailed: isChannelSettingsLoadFailed({
      loaded: state.apiCredentialsLoaded,
      failureCount: state.apiCredentialsFailureCount,
      failureCountAtDispatch: failureCountAtDispatchRef.current.apiCredentials,
    }),
    liveAgentFailed: isChannelSettingsLoadFailed({
      loaded: state.liveAgentLoaded,
      failureCount: state.liveAgentFailureCount,
      failureCountAtDispatch:
        failureCountAtDispatchRef.current.liveAgentPlatforms,
    }),
    recordDispatch,
  };
};
